import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { gbp, statusLabel } from "@/lib/invoice";

export default function InvoicePrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [company, setCompany] = useState({});
  const [settings, setSettings] = useState({});

  useEffect(() => {
    (async () => {
      const [i, c, s] = await Promise.all([
        api.get(`/admin/invoices/${id}`),
        api.get(`/site-content`),
        api.get(`/admin/invoice-settings`),
      ]);
      setInv(i.data);
      setCompany(c.data || {});
      setSettings(s.data || {});
    })();
  }, [id]);

  if (!inv) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-200 print:bg-white">
      <style>{`@media print { .no-print { display: none !important; } @page { margin: 14mm; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`}</style>

      {/* Toolbar */}
      <div className="no-print sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
        <button onClick={() => navigate(`/admin/invoices/${id}`)} className="inline-flex items-center gap-2 text-slate-600 font-semibold text-sm"><ArrowLeft className="h-4 w-4" /> Back</button>
        <button onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-green-700" data-testid="print-button"><Printer className="h-4 w-4" /> Print / Save as PDF</button>
      </div>

      {/* Sheet */}
      <div className="max-w-3xl mx-auto my-6 print:my-0 bg-white print:shadow-none shadow-lg" data-testid="invoice-print-sheet">
        <div className="p-10 print:p-0">
          {/* Header */}
          <div className="flex items-start justify-between gap-6 pb-6 border-b-2 border-green-600">
            <div>
              <img src="/logo.png" alt="Speedex International" className="h-16 w-auto" />
              <div className="mt-3 text-xs text-slate-500 leading-relaxed">
                <div className="font-bold text-slate-800">{company.company_name || "Speedex International Ltd"}</div>
                {company.contact_address && <div>{company.contact_address}</div>}
                {company.contact_phone && <div>{company.contact_phone}</div>}
                {company.contact_email && <div>{company.contact_email}</div>}
                {(company.company_number || settings.company_number) && <div>Company No. {company.company_number || settings.company_number}</div>}
                {company.registered_office && <div>Registered office: {company.registered_office}</div>}
                {settings.vat_number && <div>VAT No. {settings.vat_number}</div>}
              </div>
            </div>
            <div className="text-right">
              <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight">INVOICE</h1>
              <div className="mt-2 font-mono font-bold text-slate-700">{inv.invoice_number}</div>
              <div className="mt-1 inline-block rounded-full bg-slate-100 px-3 py-0.5 text-xs font-bold text-slate-600 uppercase">{statusLabel(inv.status)}</div>
            </div>
          </div>

          {/* Bill to + dates */}
          <div className="flex justify-between gap-6 py-6">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">Bill to</div>
              <div className="font-bold text-slate-900">{inv.customer_name}</div>
              {inv.customer_address && <div className="text-sm text-slate-600">{inv.customer_address}</div>}
              {inv.customer_postcode && <div className="text-sm text-slate-600">{inv.customer_postcode}</div>}
              {inv.customer_phone && <div className="text-sm text-slate-600">{inv.customer_phone}</div>}
              {inv.customer_email && <div className="text-sm text-slate-600">{inv.customer_email}</div>}
            </div>
            <div className="text-right text-sm">
              <div className="mb-1"><span className="text-slate-400">Issue date: </span><span className="font-semibold text-slate-800">{inv.issue_date || "—"}</span></div>
              <div><span className="text-slate-400">Due date: </span><span className="font-semibold text-slate-800">{inv.due_date || "—"}</span></div>
            </div>
          </div>

          {/* Items */}
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-900 text-white">
                <th className="text-left font-semibold py-2.5 px-3 rounded-l-lg">Description</th>
                <th className="text-right font-semibold py-2.5 px-3">Qty</th>
                <th className="text-right font-semibold py-2.5 px-3">Unit price</th>
                <th className="text-right font-semibold py-2.5 px-3 rounded-r-lg">Amount</th>
              </tr>
            </thead>
            <tbody>
              {inv.line_items.map((li, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-3 px-3 text-slate-800">{li.description}</td>
                  <td className="py-3 px-3 text-right text-slate-600">{li.quantity}</td>
                  <td className="py-3 px-3 text-right text-slate-600">{gbp(li.unit_price)}</td>
                  <td className="py-3 px-3 text-right font-semibold text-slate-800">{gbp(li.quantity * li.unit_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end mt-5">
            <div className="w-64 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-semibold">{gbp(inv.subtotal)}</span></div>
              {inv.vat_enabled && <div className="flex justify-between"><span className="text-slate-500">VAT ({inv.vat_rate}%)</span><span className="font-semibold">{gbp(inv.vat_amount)}</span></div>}
              <div className="flex justify-between bg-green-600 text-white rounded-lg px-3 py-2 mt-1"><span className="font-bold">Total</span><span className="font-display font-black">{gbp(inv.total)}</span></div>
              {inv.amount_paid > 0 && <div className="flex justify-between pt-1"><span className="text-slate-500">Paid</span><span className="font-semibold text-emerald-600">{gbp(inv.amount_paid)}</span></div>}
              {inv.amount_paid > 0 && <div className="flex justify-between"><span className="font-bold text-slate-900">Balance due</span><span className="font-bold text-amber-600">{gbp(inv.balance_due)}</span></div>}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-10 pt-6 border-t border-slate-200 text-xs text-slate-500 space-y-2">
            {inv.notes && <p><span className="font-semibold text-slate-700">Notes: </span>{inv.notes}</p>}
            {settings.bank_details && <p><span className="font-semibold text-slate-700">Payment details: </span>{settings.bank_details}</p>}
            {settings.terms && <p>{settings.terms}</p>}
            <p className="text-center pt-4 text-slate-400">Thank you for choosing {company.company_name || "Speedex International"}.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
